import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Card, Empty, ErrorState, Input, Loading, PaymentBadge, Select, Stat } from '../components/ui.jsx';
import { ConfirmModal } from '../components/Modal.jsx';
import { IconPlus } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { daysAgo, formatDate, liters, money, moneyPair, num, today, tons } from '../lib/format.js';

export default function PurchaseList() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [supplierId, setSupplierId] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const suppliers = useApi('/api/suppliers', { include_inactive: true });
  const { data, error, loading, reload } = useApi('/api/purchases', {
    from,
    to,
    supplier_id: supplierId,
    q: debouncedSearch,
    limit: 200,
  });

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/purchases/${deleting.id}`);
      toast.success(t.purchase.deleted);
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t.purchase.title}
        subtitle={t.purchase.subtitle}
        actions={
          <Link to="/buy/new" className="btn btn-sm btn-primary no-print">
            <IconPlus /> {t.common.add}
          </Link>
        }
      />

      <div className="page">
        <Card>
          <div className="filters">
            <Input label={t.common.from} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label={t.common.to} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <Select
              label={t.purchase.supplier}
              placeholder={t.party.allSuppliers}
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              options={(suppliers.data || []).map((s) => ({ value: s.id, label: s.name }))}
            />
            <Input
              className="grow"
              label={t.common.search}
              placeholder={t.purchase.searchHint}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Card>

        {data?.totals && (
          <div className="stat-grid">
            <Stat label={t.purchase.loads} value={num(data.totals.count)} />
            <Stat label={t.purchase.tonnes} value={tons(data.totals.total_tons)} hint={liters(data.totals.total_liters)} />
            <Stat
              label={t.purchase.value}
              value={moneyPair(data.totals.total_iqd, data.totals.total_usd, { zero: money(0, 'IQD') })}
            />
          </div>
        )}

        <ErrorState error={error} onRetry={reload} />
        {loading && !data && <Loading />}

        {data && data.rows.length === 0 && (
          <Card>
            <Empty
              title={t.purchase.none}
              message={t.purchase.noneHint}
              action={
                <Link to="/buy/new" className="btn btn-primary">
                  <IconPlus /> {t.purchase.record}
                </Link>
              }
            />
          </Card>
        )}

        {data && data.rows.length > 0 && (
          <div className="record-list">
            {data.rows.map((row) => (
              <div key={row.id} className="record">
                <Link to={`/buy/${row.id}/receipt`}>
                  <div className="record-top">
                    <span className="title">{row.supplier_name}</span>
                    <span className="amount">
                      {money(row.currency === 'IQD' ? row.amount_iqd : row.amount_usd, row.currency)}
                    </span>
                  </div>
                  <div className="record-meta">
                    <span className="strong">{row.batch_no}</span>
                    <span>{formatDate(row.purchased_on)}</span>
                    <span>
                      {tons(row.tons)} · {liters(row.liters)}
                    </span>
                    <span>{row.tank_name}</span>
                    <PaymentBadge type={row.payment_type} />
                  </div>
                </Link>
                {isAdmin && (
                  <div className="btn-row no-print" style={{ marginTop: 4 }}>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleting(row)}>
                      {t.common.delete}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Link to="/buy/new" className="btn btn-primary fab no-print">
        <IconPlus /> {t.purchase.newTitle}
      </Link>

      {deleting && (
        <ConfirmModal
          title={t.purchase.deleteTitle}
          message={t.purchase.deleteMessage(deleting.batch_no)}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
          busy={busy}
        />
      )}
    </>
  );
}
